import { useEffect, useState } from 'react'

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000

const ENTRY_SCRIPT_REGEX = /<script[^>]+src="(\/assets\/[A-Za-z0-9._-]+\.js)"[^>]*>/g

function pickEntryScript(html: string): string | null {
  const matches = Array.from(html.matchAll(ENTRY_SCRIPT_REGEX)).map((m) => m[1])
  if (!matches.length) return null
  const entry = matches.find((src) => /\/assets\/index-/.test(src))
  return entry || matches[0]
}

function currentEntryScript(): string | null {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
  )
  for (const s of scripts) {
    const src = s.getAttribute('src')
    if (!src) continue
    try {
      const url = new URL(src, window.location.origin)
      if (url.pathname.startsWith('/assets/') && url.pathname.endsWith('.js')) return url.pathname
    } catch {
      /* ignore */
    }
  }
  return null
}

export function useVersionCheck(intervalMs: number = DEFAULT_INTERVAL_MS) {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false)

  useEffect(() => {
    const current = currentEntryScript()
    // No hashed entry script means we're in dev (vite serves /src/index.tsx) — skip.
    if (!current) return undefined

    let cancelled = false

    const check = async () => {
      if (cancelled || newVersionAvailable) return
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        })
        if (!res.ok) return
        const html = await res.text()
        const latest = pickEntryScript(html)
        if (!cancelled && latest && latest !== current) {
          setNewVersionAvailable(true)
        }
      } catch {
        /* ignore network errors */
      }
    }

    const onFocus = () => check()
    check()
    const id = window.setInterval(check, intervalMs)
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [intervalMs, newVersionAvailable])

  return {
    newVersionAvailable,
    reload: () => window.location.reload(),
  }
}
